import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  ArrowLeft,
  CreditCard,
  Users,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Plus,
  Trash2,
  RefreshCw,
  Copy,
  Check,
  Gift,
  Calendar,
  DollarSign
} from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Badge } from '../../components/ui/Badge';
import { Modal } from '../../components/ui/Modal';
import { api } from '../../lib/api';
import { ENDPOINTS } from '../../config/api';
import { formatCurrency, formatDate } from '../../lib/utils';

interface BenefitSubscription {
  id: string;
  status: string;
  requestedAt: string;
  approvedAt?: string;
  activatedAt?: string;
  cancelledAt?: string;
  monthlyAmount: string;
  subsidyAmount: string;
  netAmount: string;
  rejectionReason?: string;
  plan: {
    id: string;
    name: string;
    category: string;
    description?: string;
    maxDependents: number;
    dependentPrice: string;
  };
  dependents: Array<{
    id: string;
    name: string;
    cpf: string;
    birthDate: string;
    relationship: string;
    monthlyPrice: string;
    isActive: boolean;
  }>;
  card?: {
    id: string;
    cardNumber: string;
    holderName: string;
    expiryDate: string;
    qrCodeData?: string;
    isActive: boolean;
  };
}

const statusConfig: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
  PENDING_APPROVAL: { label: 'Aguardando Aprovação', color: 'warning', icon: <Clock className="w-4 h-4" /> },
  ACTIVE: { label: 'Ativa', color: 'success', icon: <CheckCircle className="w-4 h-4" /> },
  SUSPENDED: { label: 'Suspensa', color: 'error', icon: <AlertCircle className="w-4 h-4" /> },
  CANCELLED: { label: 'Cancelada', color: 'error', icon: <XCircle className="w-4 h-4" /> },
  EXPIRED: { label: 'Expirada', color: 'info', icon: <Clock className="w-4 h-4" /> },
};

const relationshipLabels: Record<string, string> = {
  SPOUSE: 'Cônjuge',
  CHILD: 'Filho(a)',
  PARENT: 'Pai/Mãe',
  SIBLING: 'Irmão(ã)',
  OTHER: 'Outro',
};

const relationshipOptions = Object.entries(relationshipLabels).map(([value, label]) => ({
  value,
  label,
}));

export function SubscriptionDetails() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  
  const [copiedCard, setCopiedCard] = useState(false);
  const [isAddDependentOpen, setIsAddDependentOpen] = useState(false);
  const [isCancelOpen, setIsCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [newDependent, setNewDependent] = useState({
    name: '',
    cpf: '',
    birthDate: '',
    relationship: 'SPOUSE',
  });

  const { data: subscription, isLoading } = useQuery<BenefitSubscription>({
    queryKey: ['subscription-details', id],
    queryFn: async () => {
      const response = await api.get(ENDPOINTS.benefits.subscriptionDetails(id!));
      return response.data;
    },
    enabled: !!id,
  });

  const addDependentMutation = useMutation({
    mutationFn: async (data: typeof newDependent) => {
      const response = await api.post(ENDPOINTS.benefits.addDependent(id!), data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscription-details', id] });
      setIsAddDependentOpen(false);
      setNewDependent({ name: '', cpf: '', birthDate: '', relationship: 'SPOUSE' });
    },
  });

  const removeDependentMutation = useMutation({
    mutationFn: async (dependentId: string) => {
      const response = await api.delete(ENDPOINTS.benefits.removeDependent(id!, dependentId));
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscription-details', id] });
    },
  });

  const cancelMutation = useMutation({
    mutationFn: async (reason: string) => {
      const response = await api.delete(ENDPOINTS.benefits.cancelSubscription(id!), {
        data: { reason },
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscription-details', id] });
      setIsCancelOpen(false);
      navigate('/banking/my-subscriptions');
    },
  });

  const reissueCardMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post(ENDPOINTS.benefits.reissueCard(id!));
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscription-details', id] });
    },
  });

  const handleCopyCard = () => {
    if (subscription?.card?.cardNumber) {
      navigator.clipboard.writeText(subscription.card.cardNumber);
      setCopiedCard(true);
      setTimeout(() => setCopiedCard(false), 2000);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="h-8 bg-surface-700 rounded w-48 animate-pulse" />
        <Card className="animate-pulse">
          <div className="h-64 bg-surface-700 rounded" />
        </Card>
      </div>
    );
  }

  if (!subscription) {
    return (
      <div className="space-y-6">
        <Button variant="ghost" onClick={() => navigate('/banking/my-subscriptions')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Voltar
        </Button>
        <Card className="text-center py-12">
          <Gift className="w-12 h-12 text-surface-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-white mb-2">
            Adesão não encontrada
          </h3>
        </Card>
      </div>
    );
  }

  const statusInfo = statusConfig[subscription.status] || statusConfig.PENDING_APPROVAL;
  const canAddDependents = subscription.status === 'ACTIVE' && 
    subscription.plan.maxDependents > subscription.dependents.filter(d => d.isActive).length;

  return (
    <div className="space-y-6">
      {/* Back Button */}
      <Button variant="ghost" onClick={() => navigate('/banking/my-subscriptions')}>
        <ArrowLeft className="w-4 h-4 mr-2" />
        Voltar às Minhas Adesões
      </Button>

      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">{subscription.plan.name}</h1>
          <p className="text-surface-400 mt-1">{subscription.plan.category}</p>
        </div>
        <Badge variant={statusInfo.color as any} className="flex items-center gap-1">
          {statusInfo.icon}
          {statusInfo.label}
        </Badge>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Virtual Card */}
          {subscription.card && subscription.status === 'ACTIVE' && (
            <Card className="bg-gradient-to-br from-primary-600 to-primary-800 border-0">
              <div className="flex items-center justify-between mb-6">
                <CreditCard className="w-8 h-8 text-white/80" />
                <span className="text-white/60 text-sm">Cartão Virtual</span>
              </div>
              
              <div className="mb-6">
                <p className="text-white/60 text-xs mb-1">Número do Cartão</p>
                <div className="flex items-center gap-2">
                  <p className="text-white text-xl font-mono tracking-wider">
                    {subscription.card.cardNumber.replace(/(.{4})/g, '$1 ').trim()}
                  </p>
                  <button
                    onClick={handleCopyCard}
                    className="text-white/60 hover:text-white transition-colors"
                  >
                    {copiedCard ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="flex justify-between items-end">
                <div>
                  <p className="text-white/60 text-xs mb-1">Titular</p>
                  <p className="text-white font-medium">{subscription.card.holderName}</p>
                </div>
                <div className="text-right">
                  <p className="text-white/60 text-xs mb-1">Validade</p>
                  <p className="text-white font-medium">
                    {new Date(subscription.card.expiryDate).toLocaleDateString('pt-BR', {
                      month: '2-digit',
                      year: '2-digit',
                    })}
                  </p>
                </div>
              </div>

              {/* Reissue Button */}
              <div className="mt-4 pt-4 border-t border-white/20">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => reissueCardMutation.mutate()}
                  disabled={reissueCardMutation.isPending}
                  className="text-white/80 hover:text-white"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Solicitar Segunda Via
                </Button>
              </div>
            </Card>
          )}

          {/* Dependents */}
          <Card>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <Users className="w-5 h-5 text-primary-400" />
                Dependentes
              </h3>
              {canAddDependents && (
                <Button size="sm" onClick={() => setIsAddDependentOpen(true)}>
                  <Plus className="w-4 h-4 mr-1" />
                  Adicionar
                </Button>
              )}
            </div>

            {subscription.dependents.filter(d => d.isActive).length === 0 ? (
              <p className="text-surface-400 text-center py-6">
                Nenhum dependente cadastrado
              </p>
            ) : (
              <div className="space-y-3">
                {subscription.dependents.filter(d => d.isActive).map((dep) => (
                  <div
                    key={dep.id}
                    className="flex items-center justify-between bg-surface-800 rounded-lg p-4"
                  >
                    <div>
                      <p className="text-white font-medium">{dep.name}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge variant="info" size="sm">
                          {relationshipLabels[dep.relationship] || dep.relationship}
                        </Badge>
                        <span className="text-surface-400 text-sm">
                          +{formatCurrency(Number(dep.monthlyPrice) / 100)}/mês
                        </span>
                      </div>
                    </div>
                    {subscription.status === 'ACTIVE' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeDependentMutation.mutate(dep.id)}
                        className="text-red-400 hover:text-red-300"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* Rejection Reason */}
          {subscription.rejectionReason && (
            <Card className="bg-red-500/10 border-red-500/30">
              <h3 className="text-red-400 font-medium mb-2">Motivo da Rejeição</h3>
              <p className="text-red-300">{subscription.rejectionReason}</p>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Values */}
          <Card>
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-primary-400" />
              Valores
            </h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-surface-400">Mensalidade</span>
                <span className="text-white">{formatCurrency(Number(subscription.monthlyAmount) / 100)}</span>
              </div>
              {Number(subscription.subsidyAmount) > 0 && (
                <div className="flex justify-between">
                  <span className="text-surface-400">Subsídio</span>
                  <span className="text-green-400">-{formatCurrency(Number(subscription.subsidyAmount) / 100)}</span>
                </div>
              )}
              <div className="border-t border-surface-700 pt-3 flex justify-between">
                <span className="text-white font-medium">Valor Líquido</span>
                <span className="text-primary-400 font-bold">
                  {formatCurrency(Number(subscription.netAmount) / 100)}
                </span>
              </div>
            </div>
          </Card>

          {/* Dates */}
          <Card>
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-primary-400" />
              Datas
            </h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-surface-400">Solicitado em</span>
                <span className="text-white">{formatDate(subscription.requestedAt)}</span>
              </div>
              {subscription.approvedAt && (
                <div className="flex justify-between">
                  <span className="text-surface-400">Aprovado em</span>
                  <span className="text-white">{formatDate(subscription.approvedAt)}</span>
                </div>
              )}
              {subscription.activatedAt && (
                <div className="flex justify-between">
                  <span className="text-surface-400">Ativado em</span>
                  <span className="text-white">{formatDate(subscription.activatedAt)}</span>
                </div>
              )}
              {subscription.cancelledAt && (
                <div className="flex justify-between">
                  <span className="text-surface-400">Cancelado em</span>
                  <span className="text-white">{formatDate(subscription.cancelledAt)}</span>
                </div>
              )}
            </div>
          </Card>

          {/* Cancel Button */}
          {subscription.status === 'ACTIVE' && (
            <Button
              variant="danger"
              className="w-full"
              onClick={() => setIsCancelOpen(true)}
            >
              <XCircle className="w-4 h-4 mr-2" />
              Cancelar Adesão
            </Button>
          )}
        </div>
      </div>

      {/* Add Dependent Modal */}
      <Modal
        isOpen={isAddDependentOpen}
        onClose={() => setIsAddDependentOpen(false)}
        title="Adicionar Dependente"
      >
        <div className="space-y-4">
          <Input
            label="Nome Completo"
            value={newDependent.name}
            onChange={(e) => setNewDependent({ ...newDependent, name: e.target.value })}
            required
          />
          <Input
            label="CPF"
            value={newDependent.cpf}
            onChange={(e) => setNewDependent({ ...newDependent, cpf: e.target.value })}
            placeholder="000.000.000-00"
            required
          />
          <Input
            label="Data de Nascimento"
            type="date"
            value={newDependent.birthDate}
            onChange={(e) => setNewDependent({ ...newDependent, birthDate: e.target.value })}
            required
          />
          <Select
            label="Parentesco"
            value={newDependent.relationship}
            onChange={(e) => setNewDependent({ ...newDependent, relationship: e.target.value })}
            options={relationshipOptions}
          />
          
          <p className="text-surface-400 text-sm">
            Valor adicional: +{formatCurrency(Number(subscription.plan.dependentPrice) / 100)}/mês
          </p>

          <div className="flex justify-end gap-3 pt-4">
            <Button variant="secondary" onClick={() => setIsAddDependentOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={() => addDependentMutation.mutate(newDependent)}
              disabled={!newDependent.name || !newDependent.cpf || !newDependent.birthDate || addDependentMutation.isPending}
            >
              Adicionar
            </Button>
          </div>
        </div>
      </Modal>

      {/* Cancel Modal */}
      <Modal
        isOpen={isCancelOpen}
        onClose={() => setIsCancelOpen(false)}
        title="Cancelar Adesão"
      >
        <div className="space-y-4">
          <p className="text-surface-300">
            Tem certeza que deseja cancelar sua adesão ao plano <strong>{subscription.plan.name}</strong>?
          </p>
          <Input
            label="Motivo do Cancelamento"
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            placeholder="Informe o motivo (opcional)"
          />
          <div className="flex justify-end gap-3 pt-4">
            <Button variant="secondary" onClick={() => setIsCancelOpen(false)}>
              Voltar
            </Button>
            <Button
              variant="danger"
              onClick={() => cancelMutation.mutate(cancelReason)}
              disabled={cancelMutation.isPending}
            >
              Confirmar Cancelamento
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default SubscriptionDetails;
